import { AppleSignInButton, startGoogleRedirectSignIn, useGoogleRedirectSignIn } from "@serophin/browser";
import { type SignInResult, setRefreshTokenTransport } from "@serophin/shared";
import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { AUTH_BUTTON } from "../../components/auth/authSkin";
import { APPLE_SERVICES_ID, GOOGLE_WEB_CLIENT_ID, originPath } from "../../components/auth/signInConfig";
import type { PageProps } from "../pageProps";

/*
 * /auth/mobile?provider=google|apple|facebook - the browser leg of social sign-in for mobile builds that still use the
 * legacy contract (src/api/legacy). It ends by opening the app:
 *
 *   nottldr://auth/callback?accessToken=...&refreshToken=...&accessTokenExpiresAt=...&refreshTokenExpiresAt=...
 *
 * - google: the framework Google web redirect flow with THIS page as the redirect URI (`<origin>/auth/mobile`, which
 *   must be an authorized redirect URI of the Google web client). Opening the page starts the redirect; Google sends the
 *   browser back with ?code&state, and the code is exchanged for tokens.
 * - apple: the framework Apple web sign-in (popup, so it needs a tap on the button shown here), then the same deep link.
 * - facebook: no longer available; the page says so and links back to the app. Anything else is an invalid request.
 * Nothing is stored in this browser: the tokens only travel in the deep link.
 */

const APP_LINK = "nottldr://";
const LINK = "text-blue-600 dark:text-blue-400 underline hover:no-underline";

const openApp = (result: SignInResult): void => {
  const query = new URLSearchParams({
    accessToken: result.accessToken,
    refreshToken: result.refreshToken,
    accessTokenExpiresAt: String(result.accessTokenExpiresAt),
    refreshTokenExpiresAt: String(result.refreshTokenExpiresAt),
  });
  window.location.href = `nottldr://auth/callback?${query.toString()}`;
};

const Screen = ({ children }: { children: ComponentChildren }) => (
  <div class="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
    <div class="text-center p-6 max-w-sm">{children}</div>
  </div>
);

const Waiting = ({ text }: { text: string }) => (
  <>
    <div class="inline-block w-8 h-8 border-2 border-neutral-300 dark:border-neutral-600 border-t-blue-500 rounded-full animate-spin mb-4" />
    <p class="text-neutral-600 dark:text-neutral-400">{text}</p>
  </>
);

const BackToApp = () => (
  <p class="mt-4 text-sm">
    <a href={APP_LINK} class={LINK}>
      Back to the app
    </a>
  </p>
);

const Unavailable = ({ message }: { message: string }) => (
  <>
    <p class="text-neutral-600 dark:text-neutral-400">{message}</p>
    <BackToApp />
  </>
);

const GoogleLeg = ({ onError }: { onError: (message: string) => void }) => {
  // Declared before the hook: its effect strips ?code&state, and this one needs to know whether they were there.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!(params.has("state") && (params.has("code") || params.has("error")))) {
      startGoogleRedirectSignIn({ clientId: GOOGLE_WEB_CLIENT_ID, redirectUri: originPath("/auth/mobile") });
    }
  }, []);
  useGoogleRedirectSignIn({ redirectUri: originPath("/auth/mobile"), onSignedIn: openApp, onError, onCancelled: () => onError("Sign-in was cancelled.") });
  return <Waiting text="Redirecting to Google..." />;
};

const AppleLeg = ({ onError }: { onError: (message: string) => void }) => (
  <>
    <p class="text-neutral-600 dark:text-neutral-400 mb-4">Sign in with Apple to continue in the app.</p>
    <AppleSignInButton servicesId={APPLE_SERVICES_ID} redirectUri={originPath("/auth/mobile")} onSignedIn={openApp} onError={onError} class={AUTH_BUTTON} />
  </>
);

const Leg = ({ provider, onError }: { provider: string | null; onError: (message: string) => void }) => {
  if (provider === "google" && GOOGLE_WEB_CLIENT_ID) {
    return <GoogleLeg onError={onError} />;
  }
  if (provider === "apple" && APPLE_SERVICES_ID) {
    return <AppleLeg onError={onError} />;
  }
  if (provider === "facebook") {
    return <Unavailable message="Facebook sign-in is no longer available. Please use email to sign in." />;
  }
  return (
    <Unavailable message={provider === "google" || provider === "apple" ? "This sign-in method is not available." : "Invalid request. Missing provider."} />
  );
};

export const AuthMobilePage = (_props: PageProps) => {
  // `undefined` until mounted, so the prerender and the first client render agree.
  const [provider, setProvider] = useState<string | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // The app takes the tokens from the deep link, so the sign-in must answer with the refresh token, not put it in this browser's cookie.
    setRefreshTokenTransport("body");
    const params = new URLSearchParams(window.location.search);
    // Google sends the browser back here without ?provider, with ?code&state (or ?error&state).
    const returning = params.has("state") && (params.has("code") || params.has("error"));
    setProvider(returning ? "google" : params.get("provider"));
  }, []);

  if (provider === undefined) {
    return <Screen>{null}</Screen>;
  }
  return <Screen>{error ? <Unavailable message={error} /> : <Leg provider={provider} onError={setError} />}</Screen>;
};
